<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, email, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useStore();

const form = reactive({
  templateId: '',
  accountName: '',
  adminName: '',
  adminEmail: '',
});

const rules = {
  templateId: { required },
  accountName: { required, minLength: minLength(2) },
  adminName: { required },
  adminEmail: { required, email },
};

const v$ = useVuelidate(rules, form);

const templates = useMapGetter('accountTemplates/getAccountTemplates');
const isSubmitting = ref(false);
const errorMessage = ref('');
const result = ref(null); // { account, admin_user, generated_password }

onMounted(() => {
  store.dispatch('accountTemplates/fetchAccountTemplates');
});

const onboardingMessage = computed(() => {
  if (!result.value) return '';
  const {
    account,
    admin_user: user,
    generated_password: password,
  } = result.value;
  return t('SUPER_ADMIN.CREATE_ACCOUNT.ONBOARDING_MESSAGE', {
    accountName: account.name,
    name: user.name,
    email: user.email,
    password,
  });
});

const submit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isSubmitting.value = true;
  errorMessage.value = '';
  try {
    result.value = await store.dispatch('superAdminAccounts/createAccount', {
      template_id: form.templateId,
      account_name: form.accountName,
      admin_name: form.adminName,
      admin_email: form.adminEmail,
    });
    emit('created');
  } catch (error) {
    errorMessage.value =
      error?.message || t('SUPER_ADMIN.CREATE_ACCOUNT.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

const copyMessage = async () => {
  await copyTextToClipboard(onboardingMessage.value);
  useAlert(t('SUPER_ADMIN.CREATE_ACCOUNT.MESSAGE_COPIED'));
};
</script>

<template>
  <div class="p-6">
    <template v-if="!result">
      <h3 class="text-lg font-medium text-n-slate-12 mb-4">
        {{ t('SUPER_ADMIN.CREATE_ACCOUNT.TITLE') }}
      </h3>

      <form class="grid gap-4" @submit.prevent="submit">
        <WithLabel :label="t('SUPER_ADMIN.CREATE_ACCOUNT.TEMPLATE.LABEL')">
          <select v-model="form.templateId" class="w-full">
            <option value="" disabled>
              {{ t('SUPER_ADMIN.CREATE_ACCOUNT.TEMPLATE.PLACEHOLDER') }}
            </option>
            <option v-for="tpl in templates" :key="tpl.id" :value="tpl.id">
              {{ tpl.name }}
            </option>
          </select>
        </WithLabel>

        <WithLabel :label="t('SUPER_ADMIN.CREATE_ACCOUNT.ACCOUNT_NAME.LABEL')">
          <NextInput
            v-model="form.accountName"
            type="text"
            class="w-full"
            :placeholder="
              t('SUPER_ADMIN.CREATE_ACCOUNT.ACCOUNT_NAME.PLACEHOLDER')
            "
          />
        </WithLabel>

        <WithLabel :label="t('SUPER_ADMIN.CREATE_ACCOUNT.ADMIN_NAME.LABEL')">
          <NextInput
            v-model="form.adminName"
            type="text"
            class="w-full"
            :placeholder="
              t('SUPER_ADMIN.CREATE_ACCOUNT.ADMIN_NAME.PLACEHOLDER')
            "
          />
        </WithLabel>

        <WithLabel :label="t('SUPER_ADMIN.CREATE_ACCOUNT.ADMIN_EMAIL.LABEL')">
          <NextInput
            v-model="form.adminEmail"
            type="email"
            class="w-full"
            :placeholder="
              t('SUPER_ADMIN.CREATE_ACCOUNT.ADMIN_EMAIL.PLACEHOLDER')
            "
          />
        </WithLabel>

        <p v-if="errorMessage" class="text-n-ruby-11 text-sm">
          {{ errorMessage }}
        </p>

        <div class="flex justify-end gap-2">
          <Button
            faded
            slate
            type="button"
            :label="t('SUPER_ADMIN.CANCEL')"
            @click="emit('close')"
          />
          <Button
            type="submit"
            :is-loading="isSubmitting"
            :label="t('SUPER_ADMIN.CREATE_ACCOUNT.SUBMIT')"
          />
        </div>
      </form>
    </template>

    <template v-else>
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        {{ t('SUPER_ADMIN.CREATE_ACCOUNT.SUCCESS_TITLE') }}
      </h3>
      <p class="text-body-main text-n-slate-11 mb-4">
        {{ t('SUPER_ADMIN.CREATE_ACCOUNT.SUCCESS_DESCRIPTION') }}
      </p>

      <div
        class="bg-n-slate-2 rounded-lg p-4 mb-4 whitespace-pre-wrap text-body-main text-n-slate-12"
      >
        {{ onboardingMessage }}
      </div>

      <div class="flex justify-end gap-2">
        <Button
          faded
          slate
          :label="t('SUPER_ADMIN.CREATE_ACCOUNT.COPY_MESSAGE')"
          @click="copyMessage"
        />
        <Button
          :label="t('SUPER_ADMIN.CREATE_ACCOUNT.DONE')"
          @click="emit('close')"
        />
      </div>
    </template>
  </div>
</template>
